import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import { meetingService, Meeting } from '../services/meetingService';
import { useAuth } from '../context/AuthContext';
import JoinPreferencesModal, { JoinPreferences } from '../components/JoinPreferencesModal';
import styles from '../styles/meetingPage.module.css';

interface MeetingPageByCodeProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
  onCreateAccount?: () => void;
}

type VisitorState = 'loading' | 'new_user' | 'existing_not_joined' | 'member';

function formatMeetingTime(startTime: string | undefined): string {
  if (!startTime || !/^\d{1,2}:\d{2}$/.test(startTime)) return '';
  const [h, m] = startTime.split(':').map(Number);
  const hour = h % 12 || 12;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatTzAbbrev(tz: string | undefined): string {
  if (!tz) return '';
  const parts = tz.split('/');
  const last = parts[parts.length - 1];
  const known: Record<string, string> = {
    'Los_Angeles': 'PT', 'New_York': 'ET', 'Chicago': 'CT', 'Denver': 'MT',
    'Phoenix': 'MST', 'Anchorage': 'AKT', 'Honolulu': 'HST', 'London': 'GMT',
  };
  return known[last] || last.replace('_', ' ').slice(0, 3);
}

const MeetingPageByCode: React.FC<MeetingPageByCodeProps> = ({
  user,
  onLogout,
  onLogin,
  onCreateAccount,
}) => {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const { user: authUser } = useAuth();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [visitorState, setVisitorState] = useState<VisitorState>('loading');
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [savingToList, setSavingToList] = useState(false);
  const [joinPreferencesOpen, setJoinPreferencesOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  /** Pass phrase for meetings with join_rule === 'meeting_key'. Pre-filled from ?key= URL param. */
  const [joinKey, setJoinKey] = useState(() => searchParams.get('key') ?? '');

  const effectiveUser = user || authUser;
  const joinRule = meeting?.join_rule ?? 'anyone';
  const needsKey = joinRule === 'meeting_key';
  const canAddSelf = !needsKey || joinKey.trim().length > 0;

  usePageTracking(`Meeting: ${code || 'unknown'}`);

  useEffect(() => {
    if (!code) return;
    const load = async () => {
      try {
        setError(null);
        const m = await meetingService.getMeetingByCode(code);
        setMeeting(m);
        if (!effectiveUser) {
          setVisitorState('new_user');
          return;
        }
        const [memberStatus, homeMeetings] = await Promise.all([
          meetingService.getMemberStatus(code),
          meetingService.getHomeMeetings(),
        ]);
        setVisitorState(memberStatus.is_member ? 'member' : 'existing_not_joined');
        setIsAdmin(memberStatus.is_admin);
        setIsSaved(homeMeetings.some((h) => h.meeting_code === (m?.meeting_code || '').toUpperCase()));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Meeting not found');
        setMeeting(null);
      }
    };
    load();
  }, [code, effectiveUser]);

  const handleSaveToList = async () => {
    if (!code || !effectiveUser || savingToList) return;
    setSavingToList(true);
    try {
      await meetingService.saveToHome(code);
      setIsSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingToList(false);
    }
  };

  const handleRemoveFromList = async () => {
    if (!code || !effectiveUser || savingToList) return;
    setSavingToList(true);
    try {
      await meetingService.removeFromHome(code);
      setIsSaved(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setSavingToList(false);
    }
  };

  const handleOpenJoinPreferences = () => {
    setJoinPreferencesOpen(true);
  };

  const handleJoinWithPreferences = async (preferences: JoinPreferences) => {
    if (!code || !effectiveUser || joining) return;
    setJoining(true);
    try {
      await meetingService.joinMeeting(code, {
        ...preferences,
        ...(needsKey && joinKey.trim() ? { join_key: joinKey.trim() } : {}),
      });
      setJoinPreferencesOpen(false);
      setVisitorState('member');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add you to the meeting');
    } finally {
      setJoining(false);
    }
  };

  const handleJoinAsNewUser = () => {
    onCreateAccount ? onCreateAccount() : onLogin();
  };

  const programId = (meeting?.program_id || 'saa').toLowerCase();
  const programLabel = (meeting?.program_id || 'SAA').toUpperCase();
  const meetingFormatLabel = meeting
    ? meeting.format === 'telemeeting'
      ? 'Telemeeting'
      : meeting.format === 'hybrid'
        ? 'Hybrid'
        : meeting.format === 'in_person'
          ? 'In-Person'
          : 'Online'
    : '';
  const meetingBreadcrumbLabel = meeting
    ? `${(meeting.name || '').trim() || meetingFormatLabel} (${meeting.meeting_code})`
    : (code || 'Meeting');
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: programLabel, path: `/${programId}`, active: false },
    { label: 'Meetings', path: `/${programId}?tab=meetings`, active: false },
    { label: meetingBreadcrumbLabel, path: '', active: true },
  ];

  if (error) {
    return (
      <div className={styles.meetingPage}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.mainContent}>
          <div className={styles.error}>
            <h2>Meeting not found</h2>
            <p>{error}</p>
            <a href={`/${programId}?tab=meetings`} className={styles.backLink}>
              ← {programLabel} meetings
            </a>
          </div>
        </main>
      </div>
    );
  }

  if (!meeting && visitorState === 'loading') {
    return (
      <div className={styles.meetingPage}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.mainContent}>
          <div className={styles.loading}>Loading meeting...</div>
        </main>
      </div>
    );
  }

  if (!meeting) return null;

  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/meetings/${meeting.meeting_code}`;
  const showLocationPublic = meeting.link_visibility === 'everyone';
  const dayLabel = meeting.schedule_day || (meeting.workshop_first_session_date && (() => {
    try {
      const d = new Date(meeting.workshop_first_session_date + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'long' });
    } catch {
      return '';
    }
  })());
  const timeStr = formatMeetingTime(meeting.schedule_start_time);
  const tzStr = formatTzAbbrev(meeting.timezone);
  const timeAndTz = [timeStr, tzStr].filter(Boolean).join(' ');
  const locationLabel = meeting.location?.trim() || (meeting.format === 'telemeeting' ? 'Telemeeting' : meeting.format === 'hybrid' ? 'Hybrid' : meeting.format === 'in_person' ? 'In-person' : 'Online');
  const isMember = visitorState === 'member';

  const handleCopyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {});
  };

  return (
    <div className={styles.meetingPage}>
      <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
      <main className={styles.mainContent}>
        {/* #1 Core meeting info — everyone */}
        <div className={isMember ? styles.coreBlockCompact : styles.coreBlock}>
          <a href={`/${programId}?tab=meetings`} className={styles.backToMeetingsLink}>
            ← {programLabel} meetings
          </a>
          <h1 className={styles.meetingName}>{meeting.name}</h1>
          <p className={styles.meetingMeta}>
            {meeting.kind === 'workshop' ? 'Workshop' : 'Meeting'} · {meeting.program_id?.toUpperCase()}
            {isMember && (dayLabel || timeAndTz) && ` · ${[dayLabel, timeAndTz].filter(Boolean).join(' · ')}`}
            {isMember && showLocationPublic && (meeting.location || meeting.format) && ` · ${locationLabel}`}
          </p>
          {!isMember && (dayLabel || timeAndTz) && (
            <p className={styles.meetingSchedule}>
              {[dayLabel, timeAndTz].filter(Boolean).join(' · ')}
            </p>
          )}
          {!isMember && showLocationPublic && (meeting.location || meeting.format) && (
            <p className={styles.meetingLocation}>{locationLabel}</p>
          )}
          {meeting.description?.trim() && (
            <p className={styles.meetingDescription}>{meeting.description}</p>
          )}
        </div>

        {/* #2 Join — not logged in */}
        {visitorState === 'new_user' && (
          <div className={styles.joinBlock}>
            <p className={styles.joinIntro}>
              Want to join this meeting? Sign in or create an account to add yourself and get the join link, member list, and meeting updates.
            </p>
            <button type="button" onClick={handleJoinAsNewUser} className={styles.primaryButton}>
              Sign in or create account
            </button>
            <p className={styles.verifyHint}>
              After creating an account, check your email to verify. Once verified, return to this page and click &quot;Add me to this meeting&quot;.
            </p>
          </div>
        )}

        {/* #2 Join — logged in, not member */}
        {effectiveUser && visitorState !== 'member' && visitorState !== 'new_user' && (
          <div className={styles.joinBlock}>
            <p className={styles.saveToListLine}>
              {isSaved ? (
                <button
                  type="button"
                  onClick={handleRemoveFromList}
                  disabled={savingToList}
                  className={styles.saveToListLink}
                >
                  {savingToList ? '...' : 'Remove from my list'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSaveToList}
                  disabled={savingToList}
                  className={styles.saveToListLink}
                >
                  {savingToList ? 'Saving...' : 'Save to my list'}
                </button>
              )}
            </p>
            {joinRule === 'approval_required' && (
              <p className={styles.joinMessage}>
                This meeting requires approval to join. Request access from a meeting contact.
              </p>
            )}
            {joinRule === 'invite_only' && (
              <p className={styles.joinMessage}>
                This meeting is invite-only. Contact a meeting contact for an invitation.
              </p>
            )}
            {(joinRule === 'anyone' || joinRule === 'meeting_key') && (
              <>
                <p className={styles.joinIntro}>
                  Join this meeting to get the link and member list.
                </p>
                {needsKey && (
                  <>
                    <label htmlFor="meeting-join-key" className={styles.keyLabel}>
                      Pass phrase
                    </label>
                    <input
                      id="meeting-join-key"
                      type="text"
                      value={joinKey}
                      onChange={(e) => setJoinKey(e.target.value)}
                      placeholder="Enter pass phrase, or use the join link from the meeting"
                      className={styles.keyInput}
                      autoComplete="off"
                    />
                    <p className={styles.keyHint}>
                      If someone shared a join link with you, open that link instead of typing the phrase.
                    </p>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleOpenJoinPreferences}
                  disabled={joining || !canAddSelf}
                  className={styles.primaryButton}
                >
                  {joining ? 'Adding…' : 'Add me to this meeting'}
                </button>
              </>
            )}
          </div>
        )}

        {/* #2 Join — member */}
        {visitorState === 'member' && (
          <div className={styles.joinBlock}>
            <p className={styles.memberStatus}>You&apos;re in this meeting.</p>
            <button
              type="button"
              onClick={handleRemoveFromList}
              disabled={savingToList}
              className={styles.secondaryButton}
            >
              {savingToList ? '...' : 'Remove from my list'}
            </button>
          </div>
        )}

        <JoinPreferencesModal
          isOpen={joinPreferencesOpen}
          onClose={() => setJoinPreferencesOpen(false)}
          onSubmit={handleJoinWithPreferences}
          isSubmitting={joining}
          meetingName={meeting?.name}
        />

        {/* #3 Member area — members only; #4 admin link to settings */}
        {visitorState === 'member' && (
          <div className={styles.memberArea}>
            <h2 className={styles.memberAreaHeading}>Member area</h2>

            <div className={styles.shareCard}>
              <span className={styles.shareLabel}>Share this meeting</span>
              <div className={styles.shareRow}>
                <code className={styles.shareUrl}>{shareUrl}</code>
                <button type="button" onClick={handleCopyShareUrl} className={styles.copyButton}>
                  Copy
                </button>
              </div>
            </div>

            {isAdmin && (
              <a href={`/meetings/${meeting.meeting_code}/settings`} className={styles.operationsLink}>
                Meeting settings &amp; operations →
              </a>
            )}

            <section className={styles.memberCard} aria-labelledby="running-heading">
              <h2 id="running-heading" className={styles.memberCardTitle}>Join link &amp; running the meeting</h2>
              <p className={styles.memberCardPlaceholder}>Leader&apos;s script and readings — coming soon.</p>
            </section>
            <section className={styles.memberCard} aria-labelledby="updates-heading">
              <h2 id="updates-heading" className={styles.memberCardTitle}>Updates</h2>
              <p className={styles.memberCardPlaceholder}>Announcements, calendar, meeting notes — coming soon.</p>
            </section>
            <section className={styles.memberCard} aria-labelledby="people-heading">
              <h2 id="people-heading" className={styles.memberCardTitle}>People</h2>
              <p className={styles.memberCardPlaceholder}>Member list and service positions — coming soon.</p>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default MeetingPageByCode;
